import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Building2, Clock, Briefcase, Users, AlertCircle } from 'lucide-react';
import Button from '../components/ui/Button';
import { supabase } from '../lib/supabase';

interface BusinessDetails {
  type: string;
  size: string;
  industry: string;
  schedule: string;
  startDate: string;
}

export default function ServiceDetails() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasExistingService, setHasExistingService] = useState(false);
  const [businessDetails, setBusinessDetails] = useState<BusinessDetails>({
    type: '',
    size: '',
    industry: '',
    schedule: '',
    startDate: ''
  });

  useEffect(() => {
    fetchServiceDetails();
    checkExistingService();
  }, [serviceId]);

  async function checkExistingService() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: existingServices, error: fetchError } = await supabase
        .from('service_progress')
        .select('id')
        .eq('user_id', user.id)
        .eq('active', true);

      if (fetchError) throw fetchError;
      setHasExistingService(existingServices && existingServices.length > 0);
    } catch (error) {
      console.error('Error checking existing services:', error);
    }
  }

  async function fetchServiceDetails() {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('id', serviceId)
        .single();

      if (error) throw error;
      setService(data);
    } catch (error) {
      console.error('Error fetching service:', error);
      setError('Failed to load service details');
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) throw new Error('User not authenticated');

      if (hasExistingService) {
        const { error: updateError } = await supabase
          .from('service_progress')
          .update({ active: false })
          .eq('user_id', user.id)
          .eq('active', true);

        if (updateError) throw updateError;
      }

      const { error: progressError } = await supabase
        .from('service_progress')
        .insert([
          {
            service_id: serviceId,
            user_id: user.id,
            status: 'pending',
            progress: 0,
            business_details: businessDetails,
            active: true
          }
        ]);

      if (progressError) throw progressError;

      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting service request:', error);
      setError(error instanceof Error ? error.message : 'Failed to submit service request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Service not found</h2>
          <Button onClick={() => navigate('/services')} className="mt-4">
            Back to Services
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-lg overflow-hidden"
      >
        <div className="p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">{service.name}</h1>
          <p className="text-lg text-gray-600 mb-8">{service.description}</p>

          {hasExistingService && (
            <div className="mb-6 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>
                You already have an active service. Selecting this service will replace your current one.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Type
              </label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <select
                  required
                  value={businessDetails.type}
                  onChange={(e) => setBusinessDetails({ ...businessDetails, type: e.target.value })}
                  className="pl-10 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select business type</option>
                  <option value="startup">Startup</option>
                  <option value="small_business">Small Business</option>
                  <option value="enterprise">Enterprise</option>
                  <option value="freelancer">Freelancer</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Industry
              </label>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <select
                  required
                  value={businessDetails.industry}
                  onChange={(e) => setBusinessDetails({ ...businessDetails, industry: e.target.value })}
                  className="pl-10 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select industry</option>
                  <option value="technology">Technology</option>
                  <option value="retail">Retail</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="finance">Finance</option>
                  <option value="education">Education</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Size
              </label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <select
                  required
                  value={businessDetails.size}
                  onChange={(e) => setBusinessDetails({ ...businessDetails, size: e.target.value })}
                  className="pl-10 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select company size</option>
                  <option value="1-10">1-10 employees</option>
                  <option value="11-50">11-50 employees</option>
                  <option value="51-200">51-200 employees</option>
                  <option value="201-500">201-500 employees</option>
                  <option value="500+">500+ employees</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Schedule
              </label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <select
                  required
                  value={businessDetails.schedule}
                  onChange={(e) => setBusinessDetails({ ...businessDetails, schedule: e.target.value })}
                  className="pl-10 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select preferred schedule</option>
                  <option value="asap">As soon as possible</option>
                  <option value="within_week">Within a week</option>
                  <option value="within_month">Within a month</option>
                  <option value="flexible">Flexible</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Start Date
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="date"
                  required
                  value={businessDetails.startDate}
                  onChange={(e) => setBusinessDetails({ ...businessDetails, startDate: e.target.value })}
                  className="pl-10 w-full rounded-lg border border-gray-300 p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/services')}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : hasExistingService ? 'Replace Current Service' : 'Start Service'}
              </Button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
}